import { ColumnKind } from "./shared";

export enum Operator {
  IsEmpty = "IsEmpty",
  IsNotEmpty = "IsNotEmpty",

  IsTrue = "IsTrue",
  IsFalse = "IsFalse",

  Equal = "Equals",
  NotEqual = "NotEqual",

  Contains = "Contains",
  DoesNotContain = "DoesNotContain",

  LessThan = "LessThan",
  LessThanOrEqual = "LessThanOrEqual",
  GreaterThan = "GreaterThan",
  GreaterThanOrEqual = "GreaterThanOrEqual",

  After = "After",
  Before = "Before",
  Between = "Between",
  CurrentDay = "CurrentDay",
  PreviousDay = "PreviousDay",
  LastSevenDays = "LastSevenDays",
  LastThirtyDays = "LastThirtyDays",
  LastThreeMonths = "LastThreeMonths",
  LastSixMonths = "LastSixMonths",
  LastTwelveMonths = "LastTwelveMonths",
  NextSevenDays = "NextSevenDays",
  NextThirtyDays = "NextThirtyDays",
  NextThreeMonths = "NextThreeMonths",
}

const {
  IsEmpty,
  IsNotEmpty,
  IsTrue,
  IsFalse,
  Equal,
  NotEqual,
  Contains,
  DoesNotContain,
  LessThan,
  LessThanOrEqual,
  GreaterThan,
  GreaterThanOrEqual,
  After,
  Before,
  Between,
  CurrentDay,
  PreviousDay,
  LastSevenDays,
  LastThirtyDays,
  LastThreeMonths,
  LastSixMonths,
  LastTwelveMonths,
  NextSevenDays,
  NextThirtyDays,
  NextThreeMonths,
} = Operator;

// textual representations of special values, for differentiating them visually from plain strings;
// makes it easier to reason about them in filters/sorts (e.g., "<empty>" instead of "empty")
export enum DisplayValue {
  Empty = "<empty>",
  NotEmpty = "<not empty>",
  True = "<true>",
  False = "<false>",
}

export const operatorToDisplayString = {
  [IsEmpty]: `Is ${DisplayValue.Empty}`,
  [IsNotEmpty]: `Is ${DisplayValue.NotEmpty}`,
  [IsTrue]: `Is ${DisplayValue.True}`,
  [IsFalse]: `Is ${DisplayValue.False}`,
  [Equal]: "=",
  [NotEqual]: "≠",
  [Contains]: "Contains",
  [DoesNotContain]: "Does not contain",
  [LessThan]: "<",
  [LessThanOrEqual]: "≤",
  [GreaterThan]: ">",
  [GreaterThanOrEqual]: "≥",
  [After]: "Is after",
  [Before]: "Is before",
  [Between]: "Is between",
  [CurrentDay]: "Is today",
  [PreviousDay]: "PreviousDay",
  [LastSevenDays]: "Is within the past 7 days",
  [LastThirtyDays]: "Is within the past 30 days",
  [LastThreeMonths]: "Is within the past 3 months",
  [LastSixMonths]: "Is within the past 6 months",
  [LastTwelveMonths]: "Is within the past 12 months",
  [NextSevenDays]: "Is in the next 7 days",
  [NextThirtyDays]: "Is in the next 30 days",
  [NextThreeMonths]: "Is in the next 3 months",
};

export const tagText = (name: string, operator: Operator, value: string): string => {
  const operatorSymbol = {
    [IsEmpty]: "=",
    [IsNotEmpty]: "=",
    [IsTrue]: "=",
    [IsFalse]: "=",
    [Equal]: "=",
    [NotEqual]: "≠",
    [LessThan]: "<",
    [LessThanOrEqual]: "≤",
    [Contains]: "contains",
    [DoesNotContain]: "does not contain",
    [GreaterThan]: ">",
    [GreaterThanOrEqual]: "≥",
    [After]: "is after",
    [Before]: "is before",
    [CurrentDay]: "is",
    [Between]: "is between",
    [LastSevenDays]: "is within",
    [LastThirtyDays]: "is within",
    [LastThreeMonths]: "is within",
    [LastSixMonths]: "is within",
    [LastTwelveMonths]: "is within",
    [NextSevenDays]: "is in",
    [NextThirtyDays]: "is in",
    [NextThreeMonths]: "is in",
  };

  return `${name} ${operatorSymbol[operator]} ${value}`;
};

export const operatorToDateValue = {
  [CurrentDay]: "today",
  [LastSevenDays]: "the last 7 days (incl today)",
  [LastThirtyDays]: "the last 30 days (incl today)",
  [LastThreeMonths]: "the last 3 months (incl today)",
  [LastSixMonths]: "the last 6 months (incl today)",
  [LastTwelveMonths]: "the last 12 months (incl today)",
  [NextSevenDays]: "the next 7 days",
  [NextThirtyDays]: "the next 30 days",
  [NextThreeMonths]: "the next 3 months",
};

// Given a ColumnKind, will return a list of operators for that type!
export const kindToOperators = (kind: ColumnKind): Operator[] => {
  const common = [IsEmpty, IsNotEmpty];

  const dateTimeOperators = [
    ...common,
    Equal,
    NotEqual,
    After,
    Before,
    Between,
    CurrentDay,
    LastSevenDays,
    LastThirtyDays,
    LastThreeMonths,
    LastSixMonths,
    LastTwelveMonths,
    NextSevenDays,
    NextThirtyDays,
    NextThreeMonths,
  ];

  switch (kind) {
    case ColumnKind.Boolean:
      return [...common, IsTrue, IsFalse];
    case ColumnKind.Date:
      return dateTimeOperators;
    case ColumnKind.Enum:
      return [...common, Equal, NotEqual];
    case ColumnKind.Money:
      return [
        ...common,
        Equal,
        NotEqual,
        LessThan,
        LessThanOrEqual,
        GreaterThan,
        GreaterThanOrEqual,
      ];
    case ColumnKind.Number:
      return [
        ...common,
        Equal,
        NotEqual,
        LessThan,
        LessThanOrEqual,
        GreaterThan,
        GreaterThanOrEqual,
      ];
    case ColumnKind.Text:
      return [...common, Equal, NotEqual, Contains, DoesNotContain];
    case ColumnKind.Time:
      return dateTimeOperators;
    default:
      throw new Error(`column kind '${kind}' not found`);
  }
};
